// src/finance/FinanceReports.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Grid, Paper, Typography, TextField, FormControl, InputLabel, Select, MenuItem,
    Table, TableBody, TableCell, TableHead, TableRow, Stack, Divider, Chip
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { FaHome } from 'react-icons/fa';

// ===== API fallback (use o seu apiFetch se já tiver)
const API_URL =
    (process.env.REACT_APP_API_BACKEND) || (import.meta?.env?.REACT_APP_API_BACKEND) || 'http://localhost:9002';
async function apiFetch(path, options = {}) {
    const res = await fetch(`${API_URL}${path}`, {
        headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
        ...options,
    });
    if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
    const ct = res.headers.get('content-type') || '';
    return ct.includes('application/json') ? res.json() : res.text();
}

// util
const currency = (v) => (v == null ? '-' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const asArray = (x) => (Array.isArray(x) ? x : x?.itens || x?.rows || x?.data || []);

// CSV
function exportToCsv(filename, rows) {
    if (!rows?.length) return;
    const keys = Object.keys(rows[0]);
    const esc = (v) => {
        if (v == null) return '';
        const s = String(v).replace(/"/g, '""');
        return s.includes(',') || s.includes('\n') ? `"${s}"` : s;
    };
    const csv = [keys.join(','), ...rows.map(r => keys.map(k => esc(r[k])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url; link.download = filename; link.style.display = 'none';
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export default function FinanceReports() {

    const navigate = useNavigate();

    // filtros
    const [caminhoes, setCaminhoes] = useState([]);
    const [caminhaoId, setCaminhaoId] = useState('');
    const [competencia, setCompetencia] = useState(() => new Date().toISOString().slice(0, 7)); // YYYY-MM

    // dados
    const [fechamento, setFechamento] = useState(null);
    const [vendasFormas, setVendasFormas] = useState(null);
    const [despesasCategorias, setDespesasCategorias] = useState(null);
    const [pagamentos, setPagamentos] = useState(null);
    const [extrato, setExtrato] = useState(null);


    const [params] = useSearchParams();
    useEffect(() => {
        const cid = params.get('caminhaoId');
        const comp = params.get('competencia');
        if (cid) setCaminhaoId(cid);
        if (comp) setCompetencia(comp);
    }, [params]);

    // carregar listas
    useEffect(() => {
        (async () => {
            try {
                const cams = await apiFetch('/finance/caminhoes');
                setCaminhoes(asArray(cams));
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    // carregar relatórios
    const loadAll = async () => {
        if (!caminhaoId || !competencia) return;
        const qs = `caminhaoId=${caminhaoId}&competencia=${competencia}`;
        try {
            const [f, vf, dc, pg, ex] = await Promise.all([
                apiFetch(`/finance/rel/fechamento?${qs}`),
                apiFetch(`/finance/rel/vendas-formas?${qs}`),
                apiFetch(`/finance/rel/despesas-categorias?${qs}`),
                apiFetch(`/finance/rel/pagamentos?${qs}`),
                apiFetch(`/finance/rel/extrato-diario?${qs}`),
            ]);
            setFechamento(f);
            setVendasFormas(vf);
            setDespesasCategorias(dc);
            setPagamentos(pg);
            setExtrato(ex);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => { loadAll(); }, [caminhaoId, competencia]); // auto-carrega ao mudar filtros

    const caminhaoLabel = useMemo(() => {
        const c = asArray(caminhoes).find(x => String(x.id) === String(caminhaoId));
        if (!c) return null;
        return c.identificacao ? `${c.placa} • ${c.identificacao}` : c.placa;
    }, [caminhoes, caminhaoId]);


    const baixarExtratoPDF = () => {
        if (!caminhaoId || !competencia) return;
        const url = `${API_URL}/finance/rel/extrato-diario-pdf?caminhaoId=${caminhaoId}&competencia=${competencia}`;
        // abre em nova aba (stream com Content-Disposition)
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const baixarVendasFormasPDF = () => {
        if (!caminhaoId || !competencia) return;
        const url = `${API_URL}/finance/rel/vendas-formas-pdf?caminhaoId=${caminhaoId}&competencia=${competencia}`;
        window.open(url, '_blank', 'noopener,noreferrer'); // stream com Content-Disposition
    };

    const baixarFechamentoPDF = () => {
        if (!caminhaoId || !competencia) return;
        const url = `${API_URL}/finance/rel/fechamento-pdf?caminhaoId=${caminhaoId}&competencia=${competencia}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const baixarDespesasCategoriasPDF = () => {
        if (!caminhaoId || !competencia) return;
        const url = `${API_URL}/finance/rel/despesas-categorias-pdf?caminhaoId=${caminhaoId}&competencia=${competencia}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const baixarPagamentosPDF = () => {
        if (!caminhaoId || !competencia) return;
        const url = `${API_URL}/finance/rel/pagamentos-pdf?caminhaoId=${caminhaoId}&competencia=${competencia}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
            {/* Filtros */}
            <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }}>
                <Grid container spacing={2} alignItems="center" justifyContent="center">
                    <Grid item xs={12} sm="auto">
                        <FormControl sx={{ minWidth: 280 }}>
                            <InputLabel id="lab-cam">Caminhão/Loja</InputLabel>
                            <Select
                                labelId="lab-cam"
                                label="Caminhão/Loja"
                                value={caminhaoId}
                                onChange={(e) => setCaminhaoId(e.target.value)}
                            >
                                {asArray(caminhoes).map(c => (
                                    <MenuItem key={c.id} value={String(c.id)}>
                                        {c.identificacao ? `${c.placa} • ${c.identificacao}` : c.placa}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm="auto">
                        <TextField
                            label="Competência"
                            type="month"
                            value={competencia}
                            onChange={(e) => setCompetencia(e.target.value)}
                            inputProps={{ max: '9999-12' }}
                            sx={{ minWidth: 200 }}
                        />
                    </Grid>

                    <Grid item xs={12} sm="auto">
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" onClick={loadAll}>Atualizar</Button>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} sm="auto">
                        {caminhaoLabel && <Chip label={caminhaoLabel} />}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <button
                            style={{ width: '120px', height: '40px', borderRadius: 10, backgroundColor: '#6a0dad', color: '#fff', }}
                            variant="primary"
                            size="small"
                            onClick={() => {
                                navigate('/finance');
                            }}
                        >
                            <FaHome style={{ marginRight: '8px' }} /> {/* Ícone à esquerda */}
                            Voltar
                        </button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Fechamento */}
            <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }}>
                <Grid container spacing={2}>
                    <Typography variant="subtitle1" gutterBottom style={{ marginLeft: 20 }}>Fechamento do ciclo</Typography>
                    <Button size="small" onClick={baixarFechamentoPDF}>PDF</Button>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}><InfoLine label="Competência" value={fechamento?.competencia || '-'} /></Grid>
                    <Grid item xs={12} sm={6} md={3}><InfoLine label="Carga inicial" value={currency(fechamento?.carga_inicial)} /></Grid>
                    <Grid item xs={12} sm={6} md={3}><InfoLine label="Recargas" value={currency(fechamento?.recargas)} /></Grid>
                    <Grid item xs={12} sm={6} md={3}><InfoLine label="Vendas (líq.)" value={currency(fechamento?.vendas_liquidas)} /></Grid>
                    <Grid item xs={12} sm={6} md={3}><InfoLine label="Saldo do caminhão" value={currency(fechamento?.saldo_caminhao)} bold /></Grid>
                    <Grid item xs={12} sm={6} md={3}><InfoLine label="Despesas (info)" value={currency(fechamento?.total_despesas)} /></Grid>
                    <Grid item xs={12} sm={6} md={3}><InfoLine label="Pagamentos (info)" value={currency(fechamento?.total_pagamentos)} /></Grid>
                </Grid>
            </Paper>

            <Grid container spacing={2}>
                {/* Vendas por forma */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, borderRadius: 3 }}>
                        <Header title="Vendas por forma">
                            <Button size="small"
                                onClick={() => vendasFormas?.itens && exportToCsv(`vendas_formas_${caminhaoId}_${competencia}.csv`, vendasFormas.itens)}>
                                CSV
                            </Button>
                            <Button size="small" onClick={baixarVendasFormasPDF}>
                                PDF
                            </Button>
                        </Header>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Forma</TableCell>
                                    <TableCell align="right">Entradas</TableCell>
                                    <TableCell align="right">Saídas</TableCell>
                                    <TableCell align="right"><b>Líquido</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {asArray(vendasFormas?.itens).map(r => (
                                    <TableRow key={r.id || r.codigo}>
                                        <TableCell>{r.descricao || r.codigo}</TableCell>
                                        <TableCell align="right">{currency(r.entradas)}</TableCell>
                                        <TableCell align="right" style={{ color: '#d32f2f' }}>
                                            {currency(r.saidas)}
                                        </TableCell>
                                        <TableCell align="right">-</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">
                            Total venda líquida: <b>{currency(vendasFormas?.totalGeralLiquido || 0)}</b>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total de descontos: <b>{currency(vendasFormas?.descontos || 0)}</b>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total de promissorias: <b>{currency(vendasFormas?.promissorias || 0)}</b>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total de fiado recebido: <b>{currency(vendasFormas?.fiados_recebido || 0)}</b>
                        </Typography>
                    </Paper>
                </Grid>

                {/* Despesas por categoria */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, borderRadius: 3 }}>
                        <Header title="Despesas por categoria">
                            <Button size="small"
                                onClick={() => despesasCategorias?.itens && exportToCsv(`despesas_categorias_${caminhaoId}_${competencia}.csv`, despesasCategorias.itens)}>
                                CSV
                            </Button>
                            <Button size="small" onClick={baixarDespesasCategoriasPDF}>
                                PDF
                            </Button>
                        </Header>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Categoria</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="right">%</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {asArray(despesasCategorias?.itens).map(r => (
                                    <TableRow key={r.id || r.codigo}>
                                        <TableCell>{r.descricao || r.codigo}</TableCell>
                                        <TableCell align="right">{currency(r.total)}</TableCell>
                                        <TableCell align="right">{r.percentual?.toFixed?.(2) || r.percentual}%</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">
                            Total: <b>{currency(despesasCategorias?.total || 0)}</b>
                        </Typography>
                    </Paper>
                </Grid>

                {/* Pagamentos */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 2, borderRadius: 3 }}>
                        <Header title="Pagamentos">
                            <Button size="small"
                                onClick={() => pagamentos?.itens && exportToCsv(`pagamentos_${caminhaoId}_${competencia}.csv`, pagamentos.itens)}>
                                CSV
                            </Button>
                            <Button size="small" onClick={baixarPagamentosPDF}>
                                PDF
                            </Button>
                        </Header>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Favorecido</TableCell>
                                    <TableCell>Forma</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {asArray(pagamentos?.itens).map(r => (
                                    <TableRow key={`${r.favorecido}-${r.forma}-${r.status}`}>
                                        <TableCell>{r.favorecido}</TableCell>
                                        <TableCell>{r.forma}</TableCell>
                                        <TableCell>{r.status}</TableCell>
                                        <TableCell align="right">{currency(r.total)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">
                            Total: <b>{currency(pagamentos?.total || 0)}</b>
                        </Typography>
                    </Paper>
                </Grid>

                {/* Extrato diário */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 2, borderRadius: 3 }}>
                        <Header title="Extrato diário">
                            <Button size="small"
                                onClick={() => extrato?.dias && exportToCsv(`extrato_${caminhaoId}_${competencia}.csv`, extrato.dias)}>
                                CSV
                            </Button>
                            <Button size="small" onClick={baixarExtratoPDF}>
                                PDF
                            </Button>
                        </Header>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Data</TableCell>
                                    <TableCell align="right">Vendas (líq.)</TableCell>
                                    <TableCell align="right">Despesas</TableCell>
                                    <TableCell align="right">Pagamentos</TableCell>
                                    <TableCell align="right"><b>Saldo parcial (caminhão)</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {asArray(extrato?.dias).map(r => (
                                    <TableRow key={r.data}>
                                        <TableCell>{r.data}</TableCell>
                                        <TableCell align="right">{currency(r.vendas_liq)}</TableCell>
                                        <TableCell align="right">{currency(r.despesas)}</TableCell>
                                        <TableCell align="right">{currency(r.pagamentos)}</TableCell>
                                        <TableCell align="right"><b>{currency(r.saldo_parcial_caminhao)}</b></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

function Header({ title, children }) {
    return (
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1">{title}</Typography>
            <Stack direction="row" spacing={1}>{children}</Stack>
        </Box>
    );
}

function InfoLine({ label, value, bold }) {
    return (
        <Box>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: bold ? 700 : 500 }}>{value}</Typography>
        </Box>
    );
}
